import { channelTypeOptions, InputType, loanOptionsConstants, lockTypeOptions, productTypeOptionsMap } from 'config'
import { svgSearch } from 'stories/assets'

export const booleanOptions = ['Yes', 'No']

const conditionDownloadFormatOptions: Record<string, string> = {
  xlsx: 'Excel',
  csv: 'CSV',
}

export const conditionFilterInputs = (
  fullLoanStatusOptions: Record<string, any>,
  categories: string[],
): Record<string, InputType> => {
  return {
    loanSection: {
      inputType: 'section',
      title: 'Loan Filter',
      span: 'full',
    },
    query: {
      inputType: 'text',
      type: 'search',
      title: 'Search Loans',
      icon: svgSearch,
    },
    status: {
      inputType: 'multiselect',
      options: fullLoanStatusOptions,
      title: 'Loan Status',
      defaultSelected: false,
    },
    type: {
      inputType: 'multiselect',
      options: channelTypeOptions,
      title: 'Channel',
      defaultSelected: false,
    },
    lock: {
      inputType: 'multiselect',
      options: lockTypeOptions,
      title: 'Lock',
      defaultSelected: false,
    },
    productType: {
      inputType: 'multiselect',
      options: productTypeOptionsMap,
      title: 'Product Type',
      defaultSelected: false,
    },
    state: {
      inputType: 'multiselect',
      options: loanOptionsConstants.states,
      title: 'State',
      defaultSelected: false,
    },
    condSection: {
      inputType: 'section',
      title: 'Condition Filter',
      span: 'full',
    },
    category: {
      inputType: 'multiselect',
      options: categories,
      title: 'Category',
      defaultSelected: false,
    },
    intext: {
      inputType: 'multiselect',
      options: booleanOptions,
      title: 'Internal',
      defaultSelected: false,
    },
    requested: {
      inputType: 'multiselect',
      options: booleanOptions,
      title: 'Requested',
      defaultSelected: false,
    },
    cleared: {
      inputType: 'multiselect',
      options: booleanOptions,
      title: 'Cleared',
      defaultSelected: false,
    },
    exception: {
      inputType: 'multiselect',
      options: booleanOptions,
      title: 'Exception',
      defaultSelected: false,
    },
    asOf: {
      inputType: 'daterange',
      title: 'As of Date',
      span: 2,
    },
    format: {
      inputType: 'select',
      options: conditionDownloadFormatOptions,
      title: 'Format',
    },
  }
}

export const loanPipelineOrderByMap: Record<string, string> = {
  'Loan #': 'no',
  Borrower: 'borrowerName',
  'Property Address': 'subjectPropertyAddress',
  'Loan Amount': 'proposedLoanAmount',
  'Date Started': 'dateStarted',
  'As Of': 'asOf',
  'Notes Date': 'noteDate',
  Purpose: 'transactionType',
  Product: 'productType',
  'Lock Price': 'lockPrice',
  Status: 'loanStatus',
  'FCI Status': 'loanStatus',
  'FCI Loan Status': 'loanStatus',
  'Lock Exp': 'lockExp',
  Broker: 'brokerCompany',
  'Loan Officer Name': 'loanOfficerName',
  'L.O': 'loanOfficerName',
  'A.E': 'accountExecutiveName',
  'U.W': 'underwriterName',
  'A.M': 'accountManagerName',
  Investor: 'investorCode',
  'Commitment Date': 'investorCommitmentDate',
  'Last Draw Issue Date': 'lastDrawIssueDate',
  'Maturity Date': 'maturityDate',
  'Loan Maturity': 'maturityDate',
  Channel: 'type',
  'Original Balance': 'originalBalance',
  'Original Loan Amount': 'originalBalance',
  'Current Balance': 'currentBalance',
  'Current Loan Amount': 'currentBalance',
  // The servicing pipeline's own balance, read from the servicing note rather than the FCI feed:
  // `Current Balance` above is that feed's copy and is empty unless the book is serviced by FCI.
  UPB: 'upb',
  // Servicing Information's rate (its override when set), the same value the rate filter reads.
  'Current Interest Rate': 'currentInterestRate',
  // How many times the loan has been extended, counted from its extension records.
  'Extensions Granted': 'extensions',
  'Days Late': 'daysLate',
  'Next Due Date': 'nextDueDate',
  'Next Payment Due': 'nextDueDate',
  'Note Rate': 'noteRate',
  'FCI Note Rate': 'noteRate',
  'Total Payment': 'totalPayment',
  'Default Interest Rate': 'defaultInterestRate',
  'FCI Default Rate': 'defaultInterestRate',
  'Origination Date': 'originationDate',
  'Loan Origination': 'originationDate',
  ClosingDate: 'closingDate',
  'Program Code': 'program',
  'AP Order Date': 'appraisalOrderDate',
  'AP Inspection Date': 'appraisalInspectionDate',
  'Servicer-Company': 'servicerCompany',
  'Next Payment Date': 'nextPaymentDate',
}

export const PIPELINE_HEADER_EMAIL_ALL = 'Email All'
export const PIPELINE_ACTIONS_TITLE = 'Pipeline Actions'

export const isPipelineHeaderEmailAll = (value?: string) =>
  (value || '').trim().toLowerCase() === PIPELINE_HEADER_EMAIL_ALL.toLowerCase()

export const pipelineHeadersIncludeEmailAll = (grid?: string[][] | null) => {
  if (!grid?.length) return false
  return grid.some((row) => row.some((cell) => isPipelineHeaderEmailAll(cell)))
}

/** Base grid-config key for the pipeline type (shared across all unsaved layouts). */
export const pipelineGridBaseKey = (pipelineType?: string) =>
  pipelineType === 'Servicing' ? 'pipeline-servicing' : 'pipeline-origination'

/** Per-user column layout key; when a saved view is active, layout is scoped to that view alone. */
export const pipelineGridKey = (pipelineType?: string, activeViewId?: number | null) => {
  const base = pipelineGridBaseKey(pipelineType)
  return activeViewId != null ? `${base}-view-${activeViewId}` : base
}

/**
 * Which saved view the grid is showing, remembered per pipeline type so a reload reopens it.
 *
 * Both the Views menu and the pipeline's own Clear Filters have to be able to drop it, so the key
 * lives here rather than inside the menu: a view left set in one place while the other has reset
 * the filters is the menu naming a view the grid is not showing.
 */
export const pipelineActiveViewKey = (pipelineType?: string) => `pipeline-active-view:${pipelineType || ''}`

export type PipelineColumnFilterKind = 'text' | 'number' | 'date' | 'boolean'

/**
 * Comparisons offered per column kind, value => label, in menu order. Keep the keys in sync with
 * `columnFilterOperators` in the backend's `pipeline-column-filters.ts`; it ignores any other.
 * Numbers and dates share the backend's comparison set, only the labels differ.
 */
export const pipelineColumnFilterOperators: Record<PipelineColumnFilterKind, Record<string, string>> = {
  text: {
    contains: 'Contains',
    notContains: 'Does not contain',
    equals: 'Is',
    notEquals: 'Is not',
    startsWith: 'Starts with',
    endsWith: 'Ends with',
    isEmpty: 'Is blank',
    notEmpty: 'Is not blank',
  },
  number: {
    equals: '=',
    notEquals: '≠',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
    between: 'Between',
    isEmpty: 'Is blank',
    notEmpty: 'Is not blank',
  },
  date: {
    equals: 'On',
    notEquals: 'Not on',
    lt: 'Before',
    lte: 'On or before',
    gt: 'After',
    gte: 'On or after',
    between: 'Between',
    isEmpty: 'Is blank',
    notEmpty: 'Is not blank',
  },
  boolean: { equals: 'Is', notEquals: 'Is not' },
}

/** Operators that take no value. */
export const valuelessColumnFilterOperators = ['isEmpty', 'notEmpty']

/**
 * Header columns that can be filtered in the pipeline's first database query.
 * Keep titles in sync with `pipeline-column-filters.ts` on the backend.
 * Computed columns (current UPB, days late, extensions, ratios) are omitted on purpose.
 */
const pipelineColumnFilterBase: { title: string; kind: PipelineColumnFilterKind }[] = [
  { title: 'Loan #', kind: 'text' },
  { title: 'Borrower', kind: 'text' },
  { title: 'Property Address', kind: 'text' },
  { title: 'Full Property Address', kind: 'text' },
  { title: 'Purpose', kind: 'text' },
  { title: 'Status', kind: 'text' },
  { title: 'Servicing Status', kind: 'text' },
  { title: 'Product', kind: 'text' },
  { title: 'Channel', kind: 'text' },
  { title: 'Date Started', kind: 'date' },
  { title: 'As Of', kind: 'date' },
  { title: 'Notes Date', kind: 'date' },
  { title: 'Sub Status', kind: 'text' },
  { title: 'Lock Exp', kind: 'date' },
  { title: 'Broker', kind: 'text' },
  { title: 'Loan Officer Name', kind: 'text' },
  { title: 'L.O', kind: 'text' },
  { title: 'A.E', kind: 'text' },
  { title: 'U.W', kind: 'text' },
  { title: 'A.M', kind: 'text' },
  { title: 'Investor', kind: 'text' },
  { title: 'Commitment Date', kind: 'date' },
  { title: 'Last Draw Issue Date', kind: 'date' },
  { title: 'Maturity Date', kind: 'date' },
  { title: 'Loan Maturity', kind: 'date' },
  { title: 'ClosingDate', kind: 'date' },
  { title: 'Program Code', kind: 'text' },
  { title: 'Servicer-Company', kind: 'text' },
  { title: 'Next Payment Date', kind: 'date' },
  { title: 'Occupancy', kind: 'text' },
  { title: 'Property Type', kind: 'text' },
  { title: 'Amortization Type', kind: 'text' },
  { title: 'Warrantable Condo', kind: 'text' },
  { title: 'Citizen Type', kind: 'text' },
  { title: 'Borrower Entity', kind: 'text' },
  { title: 'Borrower Type', kind: 'text' },
  { title: 'Servicing Number', kind: 'text' },
  { title: 'Submitted Date', kind: 'date' },
  { title: 'Funded Date', kind: 'date' },
  { title: 'Subject Property County', kind: 'text' },
  { title: 'Subject Property Parcel Number', kind: 'text' },
  { title: 'First Time Home Buyer', kind: 'boolean' },
  { title: 'First Time Investor', kind: 'boolean' },
  { title: 'Loan Amount', kind: 'number' },
  { title: 'Lock Price', kind: 'number' },
  { title: 'Original Purchase Price', kind: 'number' },
  { title: 'Purchase Price', kind: 'number' },
  { title: '# of Units', kind: 'number' },
  { title: '# Units', kind: 'number' },
  { title: 'Gross rents', kind: 'number' },
  { title: 'How many experiences?', kind: 'number' },
  { title: 'Interest Reserve Amount', kind: 'number' },
  { title: 'Rehab Budget', kind: 'number' },
  { title: 'As Is Value', kind: 'number' },
  { title: 'After Repair Value', kind: 'number' },
  { title: 'Construction Reserve', kind: 'number' },
  { title: 'Original Term', kind: 'number' },
  { title: 'Current Term', kind: 'number' },
  { title: 'Amortization Term', kind: 'number' },
  { title: 'FICO', kind: 'number' },
  { title: 'Months of Reserves', kind: 'number' },
]

const pipelineColumnFilterFci: { title: string; kind: PipelineColumnFilterKind }[] = [
  { title: 'Original Balance', kind: 'number' },
  { title: 'Original Loan Amount', kind: 'number' },
  { title: 'Current Balance', kind: 'number' },
  { title: 'Current Loan Amount', kind: 'number' },
  { title: 'Days Late', kind: 'number' },
  { title: 'Total Payment', kind: 'number' },
  { title: 'Note Rate', kind: 'number' },
  { title: 'FCI Note Rate', kind: 'number' },
  { title: 'Default Interest Rate', kind: 'number' },
  { title: 'FCI Default Rate', kind: 'number' },
  { title: 'Next Due Date', kind: 'date' },
  { title: 'Next Payment Due', kind: 'date' },
  { title: 'Origination Date', kind: 'date' },
  { title: 'Loan Origination', kind: 'date' },
]

/** Grid columns Add filter never offers: Tags has its own filter above the grid. */
const pipelineColumnFilterExcluded = new Set(['', 'Tags'])

/**
 * What Add filter offers: every column on the grid. The catalog above is filtered in the
 * database; any other grid column (Current UPB, Days Late, a party, a custom field...) is matched
 * by the backend on the value the cell shows, and `kindOf` guesses its type from those cells.
 */
export const pipelineColumnFilterChoices = (
  includeFci: boolean,
  headers: string[],
  selected: string[],
  kindOf: (title: string) => PipelineColumnFilterKind = () => 'text',
) => {
  const catalog = includeFci ? [...pipelineColumnFilterBase, ...pipelineColumnFilterFci] : pipelineColumnFilterBase
  if (!headers.length) return catalog
  const shown = new Set(headers)
  const known = new Set(catalog.map((item) => item.title))
  const others = [...shown, ...selected]
    .filter((title, index, all) => all.indexOf(title) === index)
    .filter((title) => !known.has(title) && !pipelineColumnFilterExcluded.has(title))
    .map((title) => ({ title, kind: kindOf(title) }))
  return [...catalog.filter((item) => shown.has(item.title) || selected.includes(item.title)), ...others]
}

/**
 * Column filters remembered per pipeline type (Servicing, Origination, FCI). The grid reads
 * `columnFilters`; this map is what survives a refresh and a switch to another pipeline.
 */
export const columnFiltersForPipeline = (filters: Record<string, any> | undefined, pipelineType: string) => {
  const byType = filters?.columnFiltersByType
  if (byType && Array.isArray(byType[pipelineType])) return byType[pipelineType]
  if (String(filters?.pipelineType || '') === String(pipelineType || '') && Array.isArray(filters?.columnFilters))
    return filters.columnFilters
  return []
}

/** True once a row has what its operator needs: a value, or an operator that takes none. */
export const isColumnFilterComplete = (row?: { op?: string; value?: string }) =>
  !!row &&
  (valuelessColumnFilterOperators.includes(row.op || '') ||
    !!String(row.value || '')
      .replace(/\.\./g, '')
      .trim())

/**
 * "Column\\top\\tvalue" for the pipeline query. A row saved before operators existed has no `op`
 * and goes as the older "Column\\tvalue", which the backend still reads as a text search (or the
 * number range syntax). Incomplete rows are left out.
 */
export const packPipelineColumnFilters = (rows?: { column?: string; op?: string; value?: string }[]) => {
  if (!Array.isArray(rows)) return []
  return rows
    .filter((row) => row?.column && isColumnFilterComplete(row))
    .slice(0, 8)
    .map((row) => {
      const value = valuelessColumnFilterOperators.includes(row.op || '')
        ? ''
        : String(row.value).replace(/\t/g, ' ').trim()
      return row.op ? `${row.column}\t${row.op}\t${value}` : `${row.column}\t${value}`
    })
}
